import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import {
  DataGridPremium,
  GridColDef,
  GridSortCellParams,
} from '@mui/x-data-grid-premium'
import VideocamIcon from '@mui/icons-material/Videocam'
import FmdGoodIcon from '@mui/icons-material/FmdGood'
import DevicesIcon from '@mui/icons-material/Devices'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import dayjs from 'dayjs'

import { useAppSelector } from 'src/store/hooks'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { getS3ImageFilePath } from 'src/v3/utilities/image-upload'
import { dataGridSx } from '../../styles'
import type {
  EventType,
  ManageEvent,
  ManageEventsIndexTab,
} from '../manage-events.slice'
import { selectManageEventCategoryNameMap } from '../manage-events.slice'
import { categoryString } from '../utils/category'
import {
  EVENT_COLOR_VIRTUAL,
  EVENT_COLOR_IN_PERSON,
  EVENT_COLOR_HYBRID,
} from '../utils/constants'
import StatusChip from './status-chip'
import { statusDisplayLabel } from '../utils/status'

type Props = {
  events: ManageEvent[]
  loading: boolean
  tab: ManageEventsIndexTab
  emptyMessage: string
}

let eventTypeModality = (
  eventType: EventType,
): 'inPerson' | 'virtual' | 'hybrid' =>
  'virtual' in eventType
    ? 'virtual'
    : 'hybrid' in eventType
      ? 'hybrid'
      : 'inPerson'

let modalityConfig = {
  virtual: {
    Icon: VideocamIcon,
    color: EVENT_COLOR_VIRTUAL,
    label: 'Virtual',
  },
  hybrid: {
    Icon: DevicesIcon,
    color: EVENT_COLOR_HYBRID,
    label: 'Hybrid',
  },
  inPerson: {
    Icon: FmdGoodIcon,
    color: EVENT_COLOR_IN_PERSON,
    label: 'In-person',
  },
}

let thumbSx = {
  width: 64,
  height: 36,
  borderRadius: '4px',
  flexShrink: 0,
  overflow: 'hidden',
}

// Decorative: the caption names the modality, so neither the image nor the icon is announced.
let EventThumb = ({ event }: { event: ManageEvent }) => {
  let [failed, setFailed] = useState(false)
  if (event.thumbnail && !failed) {
    return (
      <Box sx={thumbSx}>
        <img
          src={getS3ImageFilePath(event.thumbnail)}
          alt=""
          onError={() => setFailed(true)}
          className="w-full h-full object-cover"
        />
      </Box>
    )
  }
  let { Icon, color } = modalityConfig[eventTypeModality(event.eventType)]
  return (
    <Box
      aria-hidden
      className="flex items-center justify-center"
      sx={{
        ...thumbSx,
        background: `color-mix(in srgb, ${color} 16%, transparent)`,
        color,
      }}
    >
      <Icon fontSize="small" />
    </Box>
  )
}

let plural = (n: number, word: string) => `${n} ${n === 1 ? word : `${word}s`}`

// Unscheduled holds both never-scheduled events and events whose sessions have all passed.
let sessionsCaption = (event: ManageEvent, tab: ManageEventsIndexTab) => {
  if (tab === 'scheduled') return plural(event.sessionCount, 'session')
  return event.sessionCount === 0
    ? 'No sessions yet'
    : plural(event.sessionCount, 'past session')
}

let formatDate = (value: Date | null) =>
  value ? dayjs(value).format('MMM D, YYYY') : '–'

let nextScheduledColumn: GridColDef<ManageEvent> = {
  field: 'nextScheduledDate',
  headerName: 'Next scheduled',
  type: 'date',
  flex: 1,
  minWidth: 160,
  valueGetter: (_value, row) =>
    row.nextScheduledDate ? new Date(row.nextScheduledDate) : null,
  valueFormatter: formatDate,
  sortComparator: (
    a: Date | null,
    b: Date | null,
    cellParamsA: GridSortCellParams,
    cellParamsB: GridSortCellParams,
  ) => {
    if (!a && !b) {
      let ua: string =
        (cellParamsA.api.getRow(cellParamsA.id) as ManageEvent)?.updatedTime ??
        ''
      let ub: string =
        (cellParamsB.api.getRow(cellParamsB.id) as ManageEvent)?.updatedTime ??
        ''
      return ub.localeCompare(ua)
    }
    if (!a) return 1
    if (!b) return -1
    return a.getTime() - b.getTime()
  },
}

let lastUpdatedColumn: GridColDef<ManageEvent> = {
  field: 'updatedTime',
  headerName: 'Last updated',
  type: 'date',
  flex: 1,
  minWidth: 160,
  valueGetter: (_value, row) =>
    row.updatedTime ? new Date(row.updatedTime) : null,
  valueFormatter: formatDate,
}

let statusColumn: GridColDef<ManageEvent> = {
  field: 'registrationStatus',
  headerName: 'Status',
  width: 200,
  valueFormatter: (value: string) =>
    statusDisplayLabel(value as ManageEvent['registrationStatus']),
  renderCell: (params) => (
    <Box className="flex items-center h-full">
      <StatusChip status={params.formattedValue as string} />
    </Box>
  ),
}

let buildColumns = (
  categoryNameMap: Record<string, string>,
  tab: ManageEventsIndexTab,
): GridColDef<ManageEvent>[] => [
  {
    field: 'name',
    headerName: 'Event',
    flex: 2,
    minWidth: 280,
    renderCell: (params) => {
      let cat = categoryString(params.row.category)
      return (
        <Box className="flex items-center h-full gap-3">
          <EventThumb event={params.row} />
          <Box className="min-w-0">
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, lineHeight: 1.3 }}
              noWrap
            >
              {params.row.name}
            </Typography>
            <Typography
              variant="caption"
              className="block"
              sx={{ color: 'text.secondary' }}
              noWrap
            >
              {[
                modalityConfig[eventTypeModality(params.row.eventType)].label,
                categoryNameMap[cat.toLowerCase()] ?? cat,
                sessionsCaption(params.row, tab),
                ...(params.row.hours != null
                  ? [`${params.row.hours} CE hrs`]
                  : []),
              ].join(' · ')}
            </Typography>
          </Box>
        </Box>
      )
    },
  },
  // Every unscheduled event has no next date and the same status, so show recency instead.
  ...(tab === 'scheduled'
    ? [nextScheduledColumn, statusColumn]
    : [lastUpdatedColumn]),
  {
    field: 'chevron',
    headerName: 'Actions',
    width: 48,
    sortable: false,
    filterable: false,
    disableExport: true,
    renderHeader: () => <span className="sr-only">Actions</span>,
    renderCell: () => (
      <Box className="flex items-center justify-center h-full">
        <ChevronRightIcon sx={{ color: 'text.secondary' }} />
      </Box>
    ),
  },
]

let ScheduledToolbar = () => (
  <Box sx={{ backgroundColor: 'background.paperDark' }}>
    <DataGridToolbar fileName="scheduled-events" showColumnsButton={false} />
  </Box>
)

let UnscheduledToolbar = () => (
  <Box sx={{ backgroundColor: 'background.paperDark' }}>
    <DataGridToolbar fileName="unscheduled-events" showColumnsButton={false} />
  </Box>
)

let toolbars = {
  scheduled: ScheduledToolbar,
  unscheduled: UnscheduledToolbar,
}

let EventsTable = ({ events, loading, tab, emptyMessage }: Props) => {
  let navigate = useNavigate()
  let categoryNameMap = useAppSelector(selectManageEventCategoryNameMap)
  let columns = useMemo(
    () => buildColumns(categoryNameMap, tab),
    [categoryNameMap, tab],
  )

  return (
    <DataGridPremium
      rows={events ?? []}
      columns={columns}
      slots={{ toolbar: toolbars[tab] }}
      showToolbar
      loading={loading}
      rowHeight={68}
      initialState={{
        pagination: { paginationModel: { page: 0, pageSize: 10 } },
        sorting: {
          sortModel:
            tab === 'scheduled'
              ? [{ field: 'nextScheduledDate', sort: 'asc' }]
              : [{ field: 'updatedTime', sort: 'desc' }],
        },
      }}
      pageSizeOptions={[10, 25, 50, 100]}
      pagination
      disableRowSelectionOnClick
      disableColumnMenu
      localeText={{ noRowsLabel: emptyMessage }}
      onRowClick={(params) => navigate(`/manage/events/${params.id}`)}
      sx={dataGridSx}
      aria-label={
        tab === 'scheduled' ? 'Scheduled events' : 'Unscheduled events'
      }
    />
  )
}

export default EventsTable
